import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSessionState } from "@/hooks/useSessionState";
import {
  fetchReferralRewards,
  fetchReferralRewardsOverview,
  markRewardAsPaid,
  markRewardAsFailed,
  ReferralReward,
} from "@/api/referrals";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import {
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Clock,
  Gift,
  TrendingUp,
  Users,
  DollarSign,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePagination } from "@/hooks/usePagination";

const ReferralRewardsList = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useSessionState("referralRewardsList.searchTerm", "");
  const [statusFilter, setStatusFilter] = useSessionState("referralRewardsList.statusFilter", "all");
  const [currentPage, setCurrentPage] = useSessionState("referralRewardsList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("referralRewardsList.pageSize", 10);
  const [selectedReward, setSelectedReward] = useState<ReferralReward | null>(
    null
  );
  const [actionType, setActionType] = useState<"paid" | "failed" | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch referral rewards overview (for cards)
  const { data: rewardsOverview } = useQuery({
    queryKey: ["referralRewardsOverview"],
    queryFn: fetchReferralRewardsOverview,
  });

  // Fetch all referral rewards (client-side pagination)
  const { data: rewardsData, isLoading } = useQuery({
    queryKey: ["referralRewards"],
    queryFn: () => fetchReferralRewards({}), // Fetch all data without pagination
  });

  // Mark reward as paid mutation
  const markAsPaidMutation = useMutation({
    mutationFn: markRewardAsPaid,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referralRewards"] });
      queryClient.invalidateQueries({ queryKey: ["referralRewardsOverview"] });
      toast({
        title: "Reward marked as paid",
        description: "The reward has been successfully marked as paid.",
      });
      setShowConfirmDialog(false);
      setSelectedReward(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to mark reward as paid. Please try again.",
        variant: "destructive",
      });
      console.error("Error marking reward as paid:", error);
    },
  });

  // Mark reward as failed mutation
  const markAsFailedMutation = useMutation({
    mutationFn: markRewardAsFailed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referralRewards"] });
      queryClient.invalidateQueries({ queryKey: ["referralRewardsOverview"] });
      toast({
        title: "Reward marked as failed",
        description: "The reward has been marked as failed.",
      });
      setShowConfirmDialog(false);
      setSelectedReward(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to mark reward as failed. Please try again.",
        variant: "destructive",
      });
      console.error("Error marking reward as failed:", error);
    },
  });

  const handleAction = (reward: ReferralReward, action: "paid" | "failed") => {
    setSelectedReward(reward);
    setActionType(action);
    setShowConfirmDialog(true);
  };

  const confirmAction = () => {
    if (!selectedReward || !actionType) return;

    if (actionType === "paid") {
      markAsPaidMutation.mutate(selectedReward.id);
    } else {
      markAsFailedMutation.mutate(selectedReward.id);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge
            variant="outline"
            className="bg-yellow-50 text-yellow-700 border-yellow-200"
          >
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "paid":
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Paid
          </Badge>
        );
      case "failed":
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-200"
          >
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredRewards =
    rewardsData?.data.filter((reward) => {
      const matchesSearch = reward.referrer_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || reward.reward_status === statusFilter;
      return matchesSearch && matchesStatus;
    }) || [];

  // Reset to page 1 when search/filter changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  // Calculate pagination (client-side)
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: filteredRewards.length,
    itemsPerPage: pageSize,
    currentPage,
  });

  // Get current page items
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentRewards = filteredRewards.slice(startIndex, endIndex);

  // Use overview stats for cards (independent of pagination)
  const totalRewards = rewardsOverview?.totalRewards || 0;
  const pendingRewards = rewardsOverview?.pendingRewards || 0;
  const paidRewards = rewardsOverview?.paidRewards || 0;
  const totalAmount = rewardsOverview?.totalAmount || 0;

  return (
    <AdminLayout title="Referral Rewards">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Rewards</CardTitle>
            <Gift className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRewards}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Rewards
            </CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingRewards}</div>
            <p className="text-xs text-muted-foreground">Awaiting payment</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Rewards</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{paidRewards}</div>
            <p className="text-xs text-muted-foreground">Successfully paid</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{totalAmount.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Total rewards value</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by referrer name..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select
                value={statusFilter}
                onValueChange={handleStatusFilterChange}
              >
                <SelectTrigger className="w-[150px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rewards Table */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : filteredRewards.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-2">No rewards found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Cycle</TableHead>
                  <TableHead>Subscribers</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Paid Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentRewards.map((reward) => (
                  <TableRow key={reward.id}>
                    <TableCell className="font-medium">
                      {reward.referrer_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        Cycle {reward.cycle_number}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1 text-muted-foreground" />
                        {reward.subscribers_in_cycle}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">
                      ₹{Number(reward.reward_amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(reward.reward_status)}
                    </TableCell>
                    <TableCell>
                      {format(new Date(reward.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      {reward.paid_at
                        ? format(new Date(reward.paid_at), "MMM d, yyyy")
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {reward.reward_status === "pending" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleAction(reward, "paid")}
                              className="text-green-600"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Mark as Paid
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleAction(reward, "failed")}
                              className="text-red-600"
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              Mark as Failed
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination Controls */}
        {filteredRewards.length > 0 && (
          <div className="flex items-center justify-between px-4 py-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Showing{" "}
              <span className="font-medium">
                {filteredRewards.length === 0
                  ? 0
                  : (currentPage - 1) * pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * pageSize, filteredRewards.length)}
              </span>{" "}
              of <span className="font-medium">{filteredRewards.length}</span>{" "}
              rewards
            </div>
            <div className="flex items-center gap-4">
              {totalPages > 0 && (
                <div className="flex items-center">
                  <Button
                    className="mr-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={!canGoPrev}
                  >
                    First
                  </Button>

                  <Button
                    className="mr-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                    }
                    disabled={!canGoPrev}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {pageNumbers.map((pageNum) => (
                    <Button
                      key={pageNum}
                      className="mx-0.5 w-8 h-8 p-0"
                      variant={pageNum === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ))}

                  <Button
                    className="ml-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(
                        currentPage < totalPages ? currentPage + 1 : totalPages
                      )
                    }
                    disabled={!canGoNext}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>

                  <Button
                    className="ml-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={!canGoNext}
                  >
                    Last
                  </Button>
                </div>
              )}

              <select
                className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
              >
                {[10, 20, 30, 50].map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Action</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this reward as {actionType}?
              {actionType === "paid" &&
                " This action will record the payment timestamp."}
              {actionType === "failed" &&
                " This action will mark the reward as failed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              className={cn(
                actionType === "paid" && "bg-green-600 hover:bg-green-700",
                actionType === "failed" && "bg-red-600 hover:bg-red-700"
              )}
            >
              {actionType === "paid" ? "Mark as Paid" : "Mark as Failed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default ReferralRewardsList;
